import React, { useState, useMemo } from 'react';
import { getStatusOptions, getStatusColor } from '../../utils/crmConstants';
import '../../styles/ticketSidebar.css';

function TicketSidebar({ tickets, selectedTicket, onTicketSelect, onRefresh, stores, pendingApprovals = [] }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [storeFilter, setStoreFilter] = useState('all');

  // Format time ago
  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return 'Just now';
  };

  // Check if ticket has pending approval
  const hasPendingApproval = (ticket) => {
    return pendingApprovals.some(
      approval => approval.ticket_number === ticket.ticket_number
    );
  };

  // Get approval info for a ticket
  const getApprovalInfo = (ticket) => {
    return pendingApprovals.find(
      approval => approval.ticket_number === ticket.ticket_number
    );
  };


  // Filter tickets
  const filteredTickets = useMemo(() => {
    let filtered = [...tickets];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(ticket =>
        (ticket.ticket_number && ticket.ticket_number.toLowerCase().includes(query)) ||
        (ticket.requester_name && ticket.requester_name.toLowerCase().includes(query)) ||
        (ticket.title && ticket.title.toLowerCase().includes(query)) ||
        (ticket.description && ticket.description.toLowerCase().includes(query))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(ticket => ticket.status === statusFilter);
    }

    // Store filter
    if (storeFilter !== 'all') {
      filtered = filtered.filter(ticket => ticket.store_pc === storeFilter);
    }

    return filtered;
  }, [tickets, searchQuery, statusFilter, storeFilter]);

  return (
    <aside className="ticket-sidebar">
      {/* Header */}
      <div className="sidebar-header">
        <h2>My Tickets</h2>
        <span className="ticket-count-badge">{filteredTickets.length}</span>
      </div>

      {/* Search Bar */}
      <div className="sidebar-search">
        <input
          type="text"
          placeholder="Search by ID, name, or description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      {/* Filters */}
      <div className="sidebar-filters">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Status</option>
          {getStatusOptions().map(status => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>

        <select
          value={storeFilter}
          onChange={(e) => setStoreFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Stores</option>
          {stores && stores.map(store => (
            <option key={store.store_pc} value={store.store_pc}>
              {store.store_pc} - {store.store_name}
            </option>
          ))}
        </select>
      </div>

      {/* Refresh Button */}
      <button className="btn-refresh-sidebar" onClick={onRefresh}>
        REFRESH
      </button>

      {/* Ticket List */}
      <div className="sidebar-ticket-list">
        {filteredTickets.length === 0 ? (
          <div className="sidebar-empty">
            <p>No tickets found</p>
          </div>
        ) : (
          filteredTickets.map(ticket => {
            const approvalInfo = getApprovalInfo(ticket);
            return (
              <div
                key={ticket.id || ticket.ticket_number}
                className={`sidebar-ticket-card ${selectedTicket?.ticket_number === ticket.ticket_number ? 'active' : ''} ${hasPendingApproval(ticket) ? 'has-approval' : ''}`}
                onClick={() => onTicketSelect(ticket)}
              >
                <div className="ticket-card-id">{ticket.ticket_number || 'N/A'}</div>
                {hasPendingApproval(ticket) && (
                  <div className="ticket-approval-badge" title={`Approval Required: ${approvalInfo?.role_label || 'Your Action Needed'}`}>
                    <span className="approval-icon">⚠️</span>
                    <span className="approval-text">Action Required</span>
                  </div>
                )}
                <div className="ticket-card-subject">
                  {ticket.title || 'No Subject'}
                </div>
                <div className="ticket-card-requester">
                  {ticket.requester_name || 'Unknown'}
                </div>
                {ticket.store_name && (
                  <div className="ticket-card-company">{ticket.store_name}</div>
                )}
                <div className="ticket-card-footer">
                  <span
                    className="status-badge-small"
                    style={{ backgroundColor: getStatusColor(ticket.status) }}
                  >
                    {(ticket.status || 'new').replace('_', ' ').toUpperCase()}
                  </span>
                  <span className="time-ago">{getTimeAgo(ticket.created_at)}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
}

export default TicketSidebar;
